import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { usePrivacy } from '../../../context/PrivacyContext';
import type { PrivacyScope } from '../../../context/PrivacyContext';

interface PrivacyToggleProps {
  /** Which page this eye governs. */
  scope: PrivacyScope;
  /** Extra class(es) appended to `privacy-toggle`. */
  className?: string;
}

/**
 * The per-page eye, dropped in beside each page's refresh control.
 *
 * It hides only its own page — the header carries the master that covers
 * everything at once. The label describes what the next press will do, rather
 * than the current state, which is what a screen reader user needs to hear.
 */
export const PrivacyToggle = memo(function PrivacyToggle({
  scope,
  className,
}: PrivacyToggleProps) {
  const { t } = useTranslation();
  const { hidden, toggle } = usePrivacy(scope);

  return (
    <button
      type="button"
      className={`privacy-toggle${hidden ? ' is-hidden' : ''}${className ? ` ${className}` : ''}`}
      onClick={toggle}
      aria-pressed={hidden}
      aria-label={t(hidden ? 'dashboard.privacy.show' : 'dashboard.privacy.hide')}
      title={t(hidden ? 'dashboard.privacy.show' : 'dashboard.privacy.hide')}
    >
      <span className="material-symbols-outlined" aria-hidden="true">
        {hidden ? 'visibility_off' : 'visibility'}
      </span>
      {/* The word carries the control. A bare 20px outline in a row of filter
          chips reads as decoration and gets missed entirely. */}
      <span className="privacy-toggle-label">
        {t(hidden ? 'dashboard.privacy.showAction' : 'dashboard.privacy.hideAction')}
      </span>
    </button>
  );
});

export default PrivacyToggle;
